import { PropType, computed, defineComponent } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";

import {
    ApplicationEnum,
    ConflictType,
    ConflictTypeEnum,
    ConflictTypeNameEnum,
    RecurrenceType,
    RecurrenceTypeEnum
} from "../../../../enums/enums";
import { formatDateInput } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { enumToMap } from "../../../../common/vue-helpers/modifiers/enum-modifiers";
import { IReassignVisitDetails } from "../../../../interfaces/i-reassign-visit";
import {
    IHomeCareTasksData,
    IHomeCareTasksParams,
    IScheduleMatchRequest,
    TaskService,
    ITaskByPatientRequest
} from "../../../../services/task.service";
import { ISelectDropDownOption } from "../../../vue-custom-elements/GroupDropdownSelect/GroupDropdownSelect";
import { IGetTaskPayorList, ITaskPayor } from "../../../../resources-vue/vue-interfaces/i-task";
import { ToastrService } from "../../../../services/toastr.service";
import { IRescheduleValidations } from "../../../../resources/dialogs/reschedule-validations/reschedule-validations";
import { IHandleTaskReschuleIssue } from "../../../../resources-vue/vue-interfaces/i-visit-details";
import { IEmployeeDetails } from "../../../vue-interfaces/i-homecare-reassign";
import { ScheduleVisitHomeCareModel } from "../../../vue-models/schedule-visit-home-care";
import eventBus from "../../../../utilities/eventBus";
import {
    IPatientProfileParams,
    PatientService,
    ICurrentEpisodeRequestParams
} from "../../../../services/patient.service";
import { AxxessIntelligenceService } from "../../../../services/axxess-intelligence.service";
import { getPatientsProfileResponse } from "../../../../common/utilities/get-patient-profile-responses";
import { ISpecialtyAxessCare } from "../../../vue-interfaces/i-visit-details";
import { VisitDetails } from "../../../../models/visit-details";
import type { ITaskDetailsPayload } from "../../../vue-interfaces/i-task-details";
import { helperTestingAgency } from "../../../../common/vue-helpers/testing-agency-check";
import { isMobileScreenSize } from "../../../../utilities/isMobileScreenSize";
import { ISelectedEmployee } from "../../../vue-interfaces/i-reassign-visit";

import FormSection from "../../../vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../vue-custom-elements/DatePicker/DatePicker.vue";
import TimePicker from "../../../vue-custom-elements/TimePicker/TimePicker.vue";
import DayPicker from "../../../vue-custom-elements/DayPicker/DayPicker.vue";
import MultiDatePickerHC from "../../../vue-custom-elements/MultiDatePickerHC/MultiDatePickerHC.vue";
import GroupDropdownSelect from "../../../vue-custom-elements/GroupDropdownSelect/GroupDropdownSelect.vue";
import CommentInput from "../../../vue-custom-elements/CommentInput/CommentInput.vue";
import MultiSelectDropdown from "../../../vue-custom-elements/MultiSelectDropdown/MultiSelectDropdown.vue";
import RescheduleValidation from "../../../vue-dialogs/RescheduleValidations/RescheduleValidation.vue";
import FindAMatch from "../../../vue-dialogs/FindAMatch/FindAMatch.vue";
import PostAxxesscare from "../../../vue-dialogs/MultipleVisitAxxesscare/MultipleVisitAxxesscare.vue";
import SaveNotifyUser from "../../../vue-custom-elements/SaveNotifyUser/SaveNotifyUser.vue";

export enum SaveEnum {
    SaveAndExit = "1",
    SaveAndAddAnother = "2",
    SaveAndNotifyEmployee = "3"
}

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        TimePicker,
        DayPicker,
        MultiDatePickerHC,
        GroupDropdownSelect,
        CommentInput,
        MultiSelectDropdown,
        SaveNotifyUser
    },

    props: {
        visitDetails: { type: Object as PropType<IReassignVisitDetails>, default: {} },
        preSelectedEmployee: { type: Object as PropType<ISelectedEmployee>, default: null },
        isLoading: { type: Boolean, default: false },
        isSaving: { type: Boolean, default: false },
        cancel: { type: Function, default: null },
        ok: { type: Function, default: null },
        addAnother: { type: Function, default: null }
    },

    setup() {
        return { v$: useVuelidate() };
    },

    validations() {
        return {
            newVisitDetails: {
                startDateTime: {
                    date: {
                        required
                    },
                    time: {
                        required: requiredIf(!this.scheduleWithoutTime)
                    }
                },
                endDateTime: {
                    date: {
                        required: requiredIf(this.overrideShiftLength)
                    },
                    time: {
                        required: requiredIf(!this.scheduleWithoutTime && this.overrideShiftLength)
                    }
                },
                payorId: { required },
                disciplineTask: { required },
                recurrence: {
                    endDate: {
                        required: requiredIf(() => {
                            return !(
                                this.newVisitDetails.recurrence.type === RecurrenceTypeEnum.Flexible ||
                                this.newVisitDetails.recurrence.type === RecurrenceTypeEnum.None
                            );
                        })
                    },
                    daysOfWeek: {
                        required: requiredIf(this.newVisitDetails.recurrence.type == RecurrenceTypeEnum.Weekly)
                    },
                    flexibleDates: {
                        required: requiredIf(this.newVisitDetails.recurrence.type == RecurrenceTypeEnum.Flexible)
                    }
                },
                discipline: { required: requiredIf(() => this.newVisitDetails.postToAxxessCare == true) }
            }
        };
    },

    data() {
        return {
            _taskService: null,
            _toastrService: null,
            _subscriptions: null,
            _axxessIntelligenceService: null,
            commentInputClass: { input: "w-35" } as { input: string; textarea?: string },
            isSavingReason: false as boolean,
            newVisitDetails: new ScheduleVisitHomeCareModel(),
            newVisitDetailsReset: ScheduleVisitHomeCareModel,
            disciplineTasks: [] as IHomeCareTasksData[],
            currentUserId: "" as string,
            employeeList: [] as IEmployeeDetails[],
            selectedConflictTypes: [ConflictTypeEnum.NoConflict] as number[],
            recurrenceType: RecurrenceType,
            recurrenceTypeEnum: RecurrenceTypeEnum,
            taskDropdownSelectOptions: {} as ISelectDropDownOption,
            employeeDropdownSelectOptions: {} as ISelectDropDownOption,
            employeeDropdownSelectList: [],
            selectedAgencies: [] as string[],
            careGivTooltip: `This filter compares employee schedules to the date and time
                                     being scheduled and sorts them into No Conflict, Partial Conflict
                                     and Full Conflict groups. No Conflict includes employees who
                                     are available at the time of the new visit. Partial Conflict includes
                                     employees who are assigned to another visit during a portion of
                                     the new visit or to tasks with no time scheduled. Full Conflict
                                     includes employees who are assigned to another visit during the
                                     entire duration of the new visit.`,
            conflictTypes: [
                {
                    name: ConflictTypeNameEnum[ConflictTypeEnum.NoConflict],
                    value: ConflictTypeEnum.NoConflict,
                    type: "conflict-type"
                },
                {
                    name: ConflictTypeNameEnum[ConflictTypeEnum.PartialConflict],
                    value: ConflictTypeEnum.PartialConflict,
                    type: "conflict-type"
                },
                {
                    name: ConflictTypeNameEnum[ConflictTypeEnum.FullConflict],
                    value: ConflictTypeEnum.FullConflict,
                    type: "conflict-type"
                }
            ],
            isEmployeeListLoading: false,
            payorList: [] as ITaskPayor[],
            isPaymentSourceLoading: false as boolean,
            isTasksLoading: false as boolean,
            checkAllByDefault: false as boolean,
            visitDates: "" as string,
            recurrence: "" as string,
            shiftLength: 1 as number,
            startTime: "" as string,
            endTime: "" as string,
            scheduleWithoutTime: false as boolean,
            displayName: null as string,
            overrideShiftLength: false as boolean,
            dialogRef: null,
            canShowPremium: false as boolean,
            _patientService: null as PatientService,
            carePeriod: "N/A" as string,
            currentCareDetailList: [],
            specialties: [] as ISpecialtyAxessCare[],
            taskDetails: {} as VisitDetails,
            currentEpisodeDetails: null,
            currentTaskDetails: [],
            showPostToAxxessCare: false,
            endDateManuallyChanged: false,
            formatDateInput,
            enumToMap,
            SaveEnum
        };
    },

    emits: ["loadingChange", "savingChange"],

    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._patientService = Container.instance.get(PatientService);
        this._axxessIntelligenceService = Container.instance.get(AxxessIntelligenceService);
    },

    async mounted() {
        const hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
        this.showPostToAxxessCare = hasTestAgency;
        await this.getVisitDetails();
        await this.getPatientCarePeriod();
        if (this.employeeIsPreSelected) {
            this.preSelectEmployee();
            this.newVisitDetails.startDateTime.date = this.preSelectedEmployee?.Date;
        }
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    computed: {
        employeeIsPreSelected() {
            return this.preSelectedEmployee !== null && Object.keys(this.preSelectedEmployee).length > 0;
        }
    },

    watch: {
        visitDates() {
            this.newVisitDetails.recurrence.flexibleDates = this.visitDates.split(", ");
        },

        startTime() {
            this.startDateChanged();
        },

        shiftLength() {
            this.shiftLengthChanged();
        },

        overrideShiftLength() {
            if (!this.overrideShiftLength) this.shiftLengthChanged();
        },

        scheduleWithoutTime() {
            this.newVisitDetails.isAllDay = this.scheduleWithoutTime;
            if (!this.scheduleWithoutTime) {
                if (this.newVisitDetails.startDateTime.date && this.newVisitDetails.endDateTime.date) {
                    this.newVisitDetails.endDateTime.date = this.newVisitDetails.startDateTime.date;
                }
            }
            this.getEmployeeList();
        },
        "newVisitDetails.startDateTime.date"() {
            this.startDateChanged();
        },

        "newVisitDetails.startDateTime.time"() {
            this.startTimeChanged();
        },

        "newVisitDetails.endDateTime.date"() {
            this.getEmployeeList();
            this.endDateChanged();
        },

        isEmployeeListLoading(newValue: boolean, oldValue: boolean) {
            if (!newValue && oldValue) {
                this.preSelectEmployee();
            }
        }
    },

    methods: {
        formatTime(dateTime: Date) {
            return dateTime
                ? new Date(dateTime).toLocaleTimeString("en-us", {
                      hour: "2-digit",
                      minute: "2-digit"
                  })
                : "";
        },

        startTimeChanged() {
            if (!this.overrideShiftLength && this.shiftLength && Number(this.shiftLength) > 0) {
                this.shiftLengthChanged();
            }
        },

        shiftLengthChanged() {
            this.newVisitDetails.lengthOfShift = this.shiftLength;
            if (this.newVisitDetails.startDateTime.date && this.newVisitDetails.startDateTime.time) {
                const startDateTime = moment(
                    `${this.newVisitDetails.startDateTime.date} ${this.newVisitDetails.startDateTime.time}`,
                    "MM/DD/YYYY hh:mm A"
                );
                const endDateTime = startDateTime.clone().add(Number(this.shiftLength), "hours");
                this.newVisitDetails.endDateTime.time = endDateTime.format("hh:mm A");
                this.newVisitDetails.endDateTime.date = endDateTime.format("MM/DD/YYYY");
            }
        },

        startDateChanged() {
            if (!this.newVisitDetails.endDateTime.date) {
                this.newVisitDetails.endDateTime.date = this.newVisitDetails.startDateTime.date;
            }
        },

        endDateChanged() {
            if (this.newVisitDetails.startDateTime.date && this.newVisitDetails.endDateTime.date) {
                this.getEmployeeList();
                this.getPaymentSource();
            }
        },

        async getDisciplineTasks() {
            try {
                const payload: IHomeCareTasksParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: ApplicationEnum.HomeCare
                };
                this.disciplineTasks = [];
                this.disciplineTasks = (await this._taskService.getHCTaskList(payload)).data.data;
                this.setDisciplineMultiSelect();
            } catch (e) {
                console.log(e);
                this._toastrService.error({
                    title: `Fetching Visit Type Failed`,
                    message: `Fetching Visit Type Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getEmployeeList() {
            try {
                if (!this.visitDetails) return;
                this.isEmployeeListLoading = true;
                this.employeeList = await this._taskService.homecareEmployeePreference({
                    PatientId: this.visitDetails.Id,
                    AgencyId: this.visitDetails.AgencyId,
                    StartDate: this.newVisitDetails.startDateTime.date ? this.newVisitDetails.startDateTime.date : "",
                    EndDate: this.newVisitDetails.endDateTime.date ? this.newVisitDetails.endDateTime.date : "",
                    SchedulerConflictTypes: this.selectedConflictTypes,
                    Application: ApplicationEnum.HomeCare,
                    LocationId: this.visitDetails.LocationId,
                    ignoreConflictTypes:
                        this.scheduleWithoutTime || this.newVisitDetails.recurrence.type != 0 ? true : false
                });
                this.employeeList = this.employeeList.map((emp) => {
                    if (!this.scheduleWithoutTime || this.newVisitDetails.recurrence.type != 0) {
                        if (emp.SchedulerConflictType == ConflictType.enum.NoConflict) {
                            emp.Icon = `<i class="fa fa-check-circle icon-green"></i>`;
                        } else if (emp.SchedulerConflictType == ConflictType.enum.PartialConflict) {
                            emp.Icon = `<i class="fa fa-exclamation-circle icon-gold"></i>`;
                        } else if (emp.SchedulerConflictType == ConflictType.enum.FullConflict) {
                            emp.Icon = `<i class="fa fa-times-circle icon-red"></i>`;
                        } else {
                            emp.Icon = "";
                        }
                    }
                    if (emp.IsFamilyProvider) {
                        emp.RelationshipIcon = `<i class="fa fa-f icon-gold ms-1"></i>`;
                    } else if (emp.Relationship == 2) {
                        emp.RelationshipIcon = `<i class="fa fa-solid fa-star icon-gold"></i>`;
                    } else if (emp.Relationship == 1) {
                        emp.RelationshipIcon = `<i class="fa-regular fa-star icon-gold"></i>`;
                    } else if (emp.Relationship == 3) {
                        emp.RelationshipIcon = `<i class="fa fa-ban"></i>`;
                    }
                    return emp;
                });

                this.setEmployeeMultiSelect();
            } catch (e) {
                throw e;
            } finally {
                this.isEmployeeListLoading = false;
            }
        },

        async setEmployeeMultiSelect() {
            try {
                this.employeeDropdownSelectOptions = {};
                if (!this.employeeList || this.employeeList?.length === 0) {
                    return;
                }
                if (this.scheduleWithoutTime || this.newVisitDetails.recurrence.type != 0) {
                    this.employeeDropdownSelectList = this.employeeList.map((child) => {
                        return {
                            displayText: child.DisplayName,
                            value: child.Id,
                            relationshipIcons: child.RelationshipIcon,
                            relationship: child.Relationship
                        };
                    });
                } else {
                    this.employeeList.forEach((employee) => {
                        const labelKey = ConflictType.getDescriptionUsingValue(employee.SchedulerConflictType);
                        this.employeeDropdownSelectOptions[labelKey] = this.employeeList
                            .filter((ele) => {
                                return ConflictType.getDescriptionUsingValue(ele.SchedulerConflictType) == labelKey;
                            })
                            .map((child) => {
                                return {
                                    displayText: child.DisplayName,
                                    value: child.Id,
                                    icons: child.Icon,
                                    relationshipIcons: child.RelationshipIcon,
                                    relationship: child.Relationship
                                };
                            });
                    });
                }
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        async setDisciplineMultiSelect() {
            try {
                this.taskDropdownSelectOptions = {};
                if (!this.disciplineTasks || this.disciplineTasks?.length === 0) {
                    return;
                }
                this.disciplineTasks.forEach((taskObject) => {
                    if (taskObject.label && Array.isArray(taskObject.children)) {
                        const labelKey = taskObject.label;
                        this.taskDropdownSelectOptions[labelKey] = taskObject.children.map((child) => {
                            return {
                                displayText: child.label,
                                value: child.id,
                                id: child.disciplineTaskId
                            };
                        });
                    }
                });
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        async getPaymentSource() {
            try {
                if (!this.visitDetails) return;
                this.isPaymentSourceLoading = true;
                const payorListPayload: IGetTaskPayorList = {
                    patientId: this.visitDetails.Id,
                    agencyId: this.visitDetails.AgencyId,
                    startDate: this.newVisitDetails.startDateTime.date,
                    endDate: this.newVisitDetails.endDateTime.date,
                    application: ApplicationEnum.HomeCare
                };
                const payors = await this._taskService.getTaskPayorsList(payorListPayload);
                this.payorList = payors.data;
            } catch (e) {
                console.log(e);
                this._toastrService.error({
                    title: `Fetching Payor List Failed`,
                    message: `Fetching Payor List Operation Failed, Please Contact Axxess if issue still persists.`
                });
            } finally {
                this.isPaymentSourceLoading = false;
            }
        },

        selectedItemsUpdated(value: number | Array<number>) {
            if (typeof value === "number") {
                if (this.selectedConflictTypes.includes(value)) {
                    const index = this.selectedConflictTypes.indexOf(value);
                    this.selectedConflictTypes.splice(index, 1);
                    this.getEmployeeList();
                    return;
                }
                this.selectedConflictTypes.push(value);
            } else {
                this.selectedConflictTypes = value;
            }
            this.getEmployeeList();
        },

        changeStartTime(time: any) {
            this.newVisitDetails.startDateTime.time = time;
        },

        changeEndTime(time: any) {
            this.newVisitDetails.endDateTime.time = time;
        },

        resultUpdated(data: any) {
            this.newVisitDetails.comments = data;
        },

        updateSelectFields(value: any, text: any, id: number) {
            this.newVisitDetails.customNoteId = value;
            this.newVisitDetails.taskName = text;
            this.newVisitDetails.disciplineTask = id;
        },

        updateSelectEmpFields(value: any, text: any, id: number) {
            this.newVisitDetails.userId = value;
            this.displayName = text;
        },

        updateResult(result: any) {
            this.visitDates = result;
        },

        handleRecurrenceChange(event: any) {
            const value = event.target.value;
            this.newVisitDetails.recurrence.type = value;
            this.newVisitDetails.recurrence.repeatBy = null;
            this.newVisitDetails.recurrence.repeatNumber = null;
            if (value == 3) {
                this.newVisitDetails.recurrence.repeatBy = 2;
                this.newVisitDetails.recurrence.repeatNumber = 1;
            } else if (value == 1 || value == 2) {
                this.newVisitDetails.recurrence.repeatNumber = 1;
            }
            if (value != 0) {
                this.selectedConflictTypes = [ConflictTypeEnum.NoConflict];
            }
            this.getEmployeeList();
        },

        taskDateChanged(date: any, name: string) {
            if (name === "startDateTime") {
                this.newVisitDetails.startDateTime.date = date;
                if (!this.endDateManuallyChanged) {
                    this.newVisitDetails.endDateTime.date = date;
                }
                if (
                    !this.overrideShiftLength &&
                    this.shiftLength &&
                    Number(this.shiftLength) > 0 &&
                    this.newVisitDetails.startDateTime.time
                ) {
                    this.shiftLengthChanged();
                }
            } else if (name === "endDateTime") {
                this.newVisitDetails.endDateTime.date = date;
                this.endDateManuallyChanged = true;
            } else if (name === "endDate") {
                this.newVisitDetails.recurrence.endDate = date;
            }
        },

        updateDaysOfWeek(value: string) {
            this.newVisitDetails.recurrence.daysOfWeek = value;
        },

        async scheduleVisit(saveType: string) {
            const valid = await this.v$.$validate();
            if (!valid) {
                return;
            }
            if (saveType == SaveEnum.SaveAndNotifyEmployee) {
                if (!this.newVisitDetails.userId) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Please select employee to notify.`
                    });
                    return;
                }
                this.newVisitDetails.NotifyUser = true;
                this.newVisitDetails.PatientName = `${this.visitDetails.FirstName} ${this.visitDetails.LastName}`;
                this.newVisitDetails.TaskName = this.newVisitDetails.taskName;
            }
            if (this.newVisitDetails.postToAxxessCare) {
                this.visitDatesChanged();
                const firstResponse = await this._taskService.scheduleVisitHC(this.newVisitDetails);
                if (!firstResponse.IsSuccessful && firstResponse.ErrorDetails) {
                    // if no error list, then simply show the error and end
                    if (!firstResponse.ErrorDetails.TaskIssues.length) {
                        throw new Error(firstResponse.ErrorMessage);
                    }

                    const validationList: IRescheduleValidations[] = firstResponse.ErrorDetails.TaskIssues.map(
                        (item: any) => {
                            return {
                                title: item.Title as string,
                                description: [item.Message] as string[],
                                taskIssueSurpressKey: item.TaskIssueSurpressKey as string
                            };
                        }
                    );
                    const hasError = firstResponse.ErrorDetails.AllTasksHaveErrors;

                    this.dialogRef = this.$dialog.open(RescheduleValidation, {
                        props: {
                            modal: true,
                            closeOnEscape: false,
                            style: {
                                width: isMobileScreenSize() ? "95vw" : "60vw"
                            },
                            showHeader: false
                        },

                        data: {
                            validations: validationList,
                            name: "Employee",
                            component: "Schedule-visit",
                            hasError
                        },

                        onClose: async (options) => {
                            if (options.data === "cancel") {
                                return;
                            } else {
                                const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                for (let issue of validationList) {
                                    issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                }

                                this.newVisitDetails.HandleTaskIssues = issuesResponseObj;
                                const secondResponse = await this._taskService.scheduleVisitHC(this.newVisitDetails);
                                if (!secondResponse.IsSuccessful) {
                                    throw new Error(secondResponse.ErrorMessage);
                                }
                                this._toastrService.success({
                                    title: `New Task Created`,
                                    message: `The task has been created.`
                                });
                                await this.getCurrentEpisode();
                                await this.getCurrentTaskDetails();

                                let visitDates: string[] = [
                                    this.newVisitDetails.startDateTime.date,
                                    ...this.newVisitDetails.recurrence.flexibleDates
                                ];
                                let currentTaskDetails: string[] = this.currentTaskDetails;
                                let newVisitDetailSpread: any = { ...this.newVisitDetails };
                                newVisitDetailSpread.PatientId = this.visitDetails.Id;
                                let disciplineTask = this.disciplineTasks.find(
                                    (task: any) => task.disciplineTask === this.newVisitDetails.disciplineTask
                                );
                                newVisitDetailSpread.visitType = this.newVisitDetails.taskName;
                                newVisitDetailSpread.customId = this.newVisitDetails.customNoteId;
                                newVisitDetailSpread.DisciplineTaskId = this.newVisitDetails.disciplineTask;
                                newVisitDetailSpread.discipline = this.newVisitDetails.discipline;
                                newVisitDetailSpread.agencyId = this.visitDetails.AgencyId;
                                newVisitDetailSpread.application = this.visitDetails.Application;
                                newVisitDetailSpread.specialityName = this.specialties.find(
                                    (task: any) => task.id === this.newVisitDetails.discipline
                                ).name;
                                newVisitDetailSpread.specialityId = this.newVisitDetails.discipline;
                                newVisitDetailSpread.eventId = currentTaskDetails;
                                newVisitDetailSpread.episodeId = this.currentEpisodeDetails.id;
                                newVisitDetailSpread.id = this.visitDetails.Id;
                                newVisitDetailSpread.userId = this.newVisitDetails.userId;
                                newVisitDetailSpread.visitDates = visitDates;
                                let data = {
                                    visitDetails: newVisitDetailSpread,
                                    taskDetails: this.taskDetails
                                };
                                this.dialogRef = await this.$dialog.open(PostAxxesscare, {
                                    props: {
                                        modal: true,
                                        showHeader: false
                                    },

                                    data: {
                                        visitDetails: data.visitDetails,
                                        taskDetails: data.taskDetails
                                    },

                                    onClose: (response) => {
                                        if (response.data == "cancel") {
                                            return;
                                        } else {
                                            if (saveType == SaveEnum.SaveAndAddAnother) {
                                                this.newVisitDetails.disciplineTask = null;
                                                this.disciplineTasks = null;
                                                this.newVisitDetails.userId = null;
                                                this.newVisitDetails.visitDates = [];
                                                this.newVisitDetails.discipline = null;
                                                this.visitDates = "";
                                            }

                                            if (
                                                saveType == SaveEnum.SaveAndExit ||
                                                saveType == SaveEnum.SaveAndNotifyEmployee
                                            ) {
                                                this.ok();
                                            }
                                        }
                                    }
                                });

                                if (saveType == SaveEnum.SaveAndExit || saveType == SaveEnum.SaveAndNotifyEmployee) {
                                    // trigger callback
                                    this.ok();
                                } else {
                                    this.addAnother();
                                }
                            }
                        }
                    });
                    return;
                } else if (!firstResponse.IsSuccessful) {
                    throw new Error(firstResponse.ErrorMessage);
                } else {
                    this._toastrService.success({
                        title: `New Task Created`,
                        message: `The task has been created.`
                    });

                    await this.getCurrentEpisode();
                    await this.getCurrentTaskDetails();

                    let visitDates: string[] = [
                        this.newVisitDetails.startDateTime.date,
                        ...this.newVisitDetails.recurrence.flexibleDates
                    ];
                    let currentTaskDetails: string[] = this.currentTaskDetails;
                    let newVisitDetailSpread: any = { ...this.newVisitDetails };
                    newVisitDetailSpread.PatientId = this.visitDetails.Id;
                    let disciplineTask = this.disciplineTasks.find(
                        (task: any) => task.disciplineTask === this.newVisitDetails.disciplineTask
                    );
                    newVisitDetailSpread.visitType = this.newVisitDetails.taskName;
                    newVisitDetailSpread.customId = this.newVisitDetails.customNoteId;
                    newVisitDetailSpread.DisciplineTaskId = this.newVisitDetails.disciplineTask;
                    newVisitDetailSpread.discipline = this.newVisitDetails.discipline;
                    newVisitDetailSpread.agencyId = this.visitDetails.AgencyId;
                    newVisitDetailSpread.application = this.visitDetails.Application;
                    newVisitDetailSpread.specialityName = this.specialties.find(
                        (task: any) => task.id === this.newVisitDetails.discipline
                    ).name;
                    newVisitDetailSpread.specialityId = this.newVisitDetails.discipline;
                    newVisitDetailSpread.eventId = currentTaskDetails;
                    newVisitDetailSpread.episodeId = this.currentEpisodeDetails.id;
                    newVisitDetailSpread.id = this.visitDetails.Id;
                    newVisitDetailSpread.userId = this.newVisitDetails.userId;
                    newVisitDetailSpread.visitDates = visitDates;
                    let data = {
                        visitDetails: newVisitDetailSpread,
                        taskDetails: this.taskDetails
                    };

                    this.dialogRef = await this.$dialog.open(PostAxxesscare, {
                        props: {
                            modal: true,
                            showHeader: false
                        },

                        data: {
                            visitDetails: data.visitDetails,
                            taskDetails: data.taskDetails
                        },

                        onClose: (options) => {
                            if (options.data == "cancel") {
                                return;
                            } else {
                                if (saveType == SaveEnum.SaveAndAddAnother) {
                                    this.newVisitDetails.disciplineTask = null;
                                    this.disciplineTasks = null;
                                    this.newVisitDetails.userId = null;
                                    this.newVisitDetails.visitDates = [];
                                    this.newVisitDetails.discipline = null;
                                    this.visitDates = "";
                                }

                                if (saveType == SaveEnum.SaveAndExit || saveType == SaveEnum.SaveAndNotifyEmployee) {
                                    this.ok();
                                }
                            }
                        }
                    });
                }
            } else {
                if (!!this.newVisitDetails) {
                    try {
                        this.$emit("savingChange", true);
                        this.visitDatesChanged();
                        const firstResponse = await this._taskService.scheduleVisitHC(this.newVisitDetails);
                        if (!firstResponse.IsSuccessful && firstResponse.ErrorDetails) {
                            // if no error list, then simply show the error and end
                            if (!firstResponse.ErrorDetails.TaskIssues.length) {
                                throw new Error(firstResponse.ErrorMessage);
                            }

                            const validationList: IRescheduleValidations[] = firstResponse.ErrorDetails.TaskIssues.map(
                                (item: any) => {
                                    return {
                                        title: item.Title as string,
                                        description: [item.Message] as string[],
                                        taskIssueSurpressKey: item.TaskIssueSurpressKey as string
                                    };
                                }
                            );
                            const hasError = firstResponse.ErrorDetails.AllTasksHaveErrors;

                            this.dialogRef = this.$dialog.open(RescheduleValidation, {
                                props: {
                                    modal: true,
                                    closeOnEscape: false,
                                    style: {
                                        width: "60vw"
                                    },
                                    showHeader: false
                                },

                                data: {
                                    validations: validationList,
                                    name: "Employee",
                                    component: "Schedule-visit",
                                    hasError
                                },

                                onClose: async (options) => {
                                    if (options.data === "cancel") {
                                        return;
                                    } else {
                                        const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                        for (let issue of validationList) {
                                            issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                        }

                                        this.newVisitDetails.HandleTaskIssues = issuesResponseObj;
                                        const secondResponse = await this._taskService.scheduleVisitHC(
                                            this.newVisitDetails
                                        );
                                        if (!secondResponse.IsSuccessful) {
                                            throw new Error(secondResponse.ErrorMessage);
                                        }
                                        this._toastrService.success({
                                            title: `New Task Created`,
                                            message: `The task has been created.`
                                        });

                                        if (
                                            saveType == SaveEnum.SaveAndExit ||
                                            saveType == SaveEnum.SaveAndNotifyEmployee
                                        ) {
                                            // trigger callback
                                            this.ok();
                                        } else {
                                            this.addAnother();
                                        }
                                    }
                                }
                            });
                            return;
                        } else if (!firstResponse.IsSuccessful) {
                            throw new Error(firstResponse.ErrorMessage);
                        } else {
                            this._toastrService.success({
                                title: `New Task Created`,
                                message: `The task has been created.`
                            });
                        }

                        if (saveType == SaveEnum.SaveAndExit || saveType == SaveEnum.SaveAndNotifyEmployee) {
                            // trigger callback
                            this.ok();
                        } else {
                            this.addAnother();
                        }
                    } catch (e) {
                        console.warn(e);
                        this._toastrService.error({
                            title: `Visit Scheduled Failed`,
                            message: e
                                ? e
                                : `Scheduling Visit Operation Failed, Please Contact Axxess if issue still persists.`
                        });
                    } finally {
                        this.$emit("savingChange", false);
                    }
                }
            }
        },

        async openFindAMatch(dropEvent: any) {
            if (this.newVisitDetails.startDateTime.date) {
                const payload: IScheduleMatchRequest = {
                    application: this.newVisitDetails.application,
                    agencyId: this.newVisitDetails.agencyId,
                    clientName: `${this.visitDetails.FirstName},${this.visitDetails.LastName}`,
                    endDate:
                        this.newVisitDetails.endDateTime?.time && !this.scheduleWithoutTime
                            ? moment(
                                  this.newVisitDetails.endDateTime.date + " " + this.newVisitDetails.endDateTime.time,
                                  "MM/DD/YYYY hh:mm A"
                              ).format("MM/DD/YYYY hh:mm A")
                            : moment(this.newVisitDetails.endDateTime.date, "MM/DD/YYYY").format("MM/DD/YYYY"),
                    startDate:
                        this.newVisitDetails.startDateTime.time && !this.scheduleWithoutTime
                            ? moment(
                                  this.newVisitDetails.startDateTime.date +
                                      " " +
                                      this.newVisitDetails.startDateTime.time,
                                  "MM/DD/YYYY hh:mm A"
                              ).format("MM/DD/YYYY hh:mm A")
                            : moment(this.newVisitDetails.startDateTime.date, "MM/DD/YYYY").format("MM/DD/YYYY"),
                    isAllDay: this.newVisitDetails.isAllDay,
                    locationId: this.visitDetails.LocationId,
                    patientId: this.visitDetails.Id,
                    payor: this.newVisitDetails.payorId,
                    task: this.newVisitDetails.taskName
                };

                this.dialogRef = this.$dialog.open(FindAMatch, {
                    props: {
                        modal: true,
                        closeOnEscape: false,
                        style: {
                            width: "82vw"
                        },
                        showHeader: false
                    },

                    data: {
                        payload
                    },

                    onClose: async (options) => {
                        if (options.data) {
                            this.newVisitDetails.userId = options.data.userId;
                            this.displayName = options.data.displayName;
                        }
                    }
                });
            } else {
                this._toastrService.error({
                    title: `Find a Match Failed`,
                    message: `Please select start date and end date in order to continue to find a match.`
                });
            }
        },

        detached() {
            this._subscriptions?.forEach((sub: any) => sub.dispose());
        },

        async getPatientCarePeriod() {
            try {
                const payload: IPatientProfileParams = {
                    agencyId: this.visitDetails.AgencyId,
                    patientId: this.visitDetails.Id,
                    application: ApplicationEnum.HomeCare
                };
                const response = await this._patientService.getPatientsProfile(payload);
                const episodeRange = await this._patientService.getEpisodeRange(payload);
                getPatientsProfileResponse.call(this, response);
                const uniqueRange = episodeRange
                    .filter((ep) => ep.Range > response.data.CarePeriod)
                    .map((ep) => ep.Range);
                this.currentCareDetailList.push(response.data.CarePeriod, ...uniqueRange);
            } catch (e) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                });
                this.cancel();
            }
        },

        onTaskTypeChange(discipline: any) {
            this.newVisitDetails.discipline = discipline;
        },

        async getSpeciality() {
            try {
                this.specialties = await this._taskService.getAxxessCareSpecialties({
                    agencyId: this.visitDetails.AgencyId,
                    application: ApplicationEnum.HomeCare,
                    discipline: 1
                });
            } catch {
                this._toastrService.error({
                    title: `Fetching Task Type Failed`,
                    message: `Fetching Task Type Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        visitDatesChanged() {
            if (this.scheduleWithoutTime) {
                this.newVisitDetails.eventStartTime = moment(
                    this.newVisitDetails.startDateTime.date + " 00:00 AM",
                    "MM/DD/YYYY hh:mm A"
                ).format("YYYY-MM-DDTHH:mm:ss");
                this.newVisitDetails.eventEndTime = moment(
                    this.newVisitDetails.endDateTime.date + " 00:00 AM",
                    "MM/DD/YYYY hh:mm A"
                ).format("YYYY-MM-DDTHH:mm:ss");
            } else {
                this.newVisitDetails.eventStartTime = moment(
                    this.newVisitDetails.startDateTime.date + " " + this.newVisitDetails.startDateTime.time,
                    "MM/DD/YYYY hh:mm A"
                ).format("YYYY-MM-DDTHH:mm:ss");
                this.newVisitDetails.eventEndTime = moment(
                    this.newVisitDetails.endDateTime.date + " " + this.newVisitDetails.endDateTime.time,
                    "MM/DD/YYYY hh:mm A"
                ).format("YYYY-MM-DDTHH:mm:ss");
            }
        },

        async getCurrentEpisode() {
            try {
                const payload: ICurrentEpisodeRequestParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application,
                    patientId: this.visitDetails.Id
                };
                this.currentEpisodeDetails = await this._patientService.getCurrentEpisode(payload);
                this.newVisitDetails.episodeId = this.currentEpisodeDetails.id;
            } catch (e) {
                this._toastrService.error({
                    title: `Fetching Current Episode Failed`,
                    message: `Fetching Current Episode Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getCurrentTaskDetails() {
            try {
                const visitDates: string[] = [
                    this.newVisitDetails.startDateTime.date,
                    ...this.newVisitDetails.recurrence.flexibleDates
                ];
                let taskWithDates: Array<{ taskId: string; startDate: string }> = [];
                for (const visitDate of visitDates) {
                    const startDate = visitDate;
                    const endDate = visitDate;

                    const payload: ITaskByPatientRequest = {
                        patientIds: [this.visitDetails.Id],
                        start: startDate,
                        end: endDate
                    };
                    const tasks = await this._taskService.getTasksByPatients(payload);

                    if (tasks && Array.isArray(tasks)) {
                        const taskIds = tasks.map((task: any) => task.Id ?? task.id).filter(Boolean);
                        taskIds.forEach((taskId) => {
                            taskWithDates.push({
                                taskId: taskId,
                                startDate: startDate
                            });
                        });
                    }
                }
                this.currentTaskDetails = taskWithDates;
            } catch (e) {
                this._toastrService.error({
                    title: "Fetching Current Task Details Failed",
                    message:
                        "Fetching Current Task Details Operation Failed, Please Contact Axxess if issue still persists."
                });
            }
        },

        async getVisitDetails() {
            Object.assign(this.visitDetails, {});
            try {
                this.$emit("loadingChange", true);
                await this.getDisciplineTasks();
                await this.getEmployeeList();
                await this.getSpeciality();
                this.newVisitDetails.application = this.visitDetails.Application;
                this.newVisitDetails.agencyId = this.visitDetails.AgencyId;
                this.newVisitDetails.patientId = this.visitDetails.Id;
                this.newVisitDetails.payorId = null;
                this.newVisitDetails.locationId = this.visitDetails.LocationId;
                this.newVisitDetailsReset = JSON.parse(JSON.stringify(this.newVisitDetails));
                const isPremiumResp = await this._axxessIntelligenceService.getAiPermissionByAgencies({
                    AgencyIds: [this.newVisitDetails.agencyId]
                });
                this.canShowPremium = isPremiumResp ? isPremiumResp.IsPremium : false;
            } catch (error) {
                console.log(error);
            } finally {
                this.$emit("loadingChange", false);
            }
        },

        preSelectEmployee() {
            if (this.employeeIsPreSelected) {
                this.updateSelectEmpFields(this.preSelectedEmployee?.Id, this.preSelectedEmployee?.DisplayName, null);
            }
        }
    }
});
